import type { ActionCommandDef } from './types';

/**
 * Module-level registry of every `//` action command. Keyed by `id`
 * for O(1) lookup; insertion order is preserved by the underlying
 * `Map` so the default catalog ordering matches the registration
 * order in `defs/index.ts`.
 *
 * Side-effect populated: each `defs/<name>.ts` file calls
 * `registerActionCommand()` at import time, and the `defs/index.ts`
 * barrel pulls them all in.
 */
const REGISTRY = new Map<string, ActionCommandDef>();

/**
 * Add an action command to the registry. Re-registering the same id
 * is a developer error (typically a copy-paste during scaffolding) —
 * we `console.error` rather than throw so a bad def in one branch
 * can't crash the whole search bar at boot.
 */
export function registerActionCommand(def: ActionCommandDef): void {
  if (REGISTRY.has(def.id)) {
    // eslint-disable-next-line no-console
    console.error(
      `[GlobalMapSearch/action] duplicate action id "${def.id}" — second registration ignored`,
    );
    return;
  }
  REGISTRY.set(def.id, def);
}

/** Return all registered action commands in insertion order. */
export function getAllActionCommands(): ActionCommandDef[] {
  return Array.from(REGISTRY.values());
}

/** Lookup a single action command by canonical id (not alias). */
export function getActionCommandById(id: string): ActionCommandDef | undefined {
  return REGISTRY.get(id);
}

/**
 * Internal: clear the registry. Exists only for unit tests, which
 * need a fresh registry per test case to avoid order-of-import
 * coupling between test files.
 */
export function _resetActionRegistryForTests(): void {
  REGISTRY.clear();
}
